import React, { useState, useEffect, useRef } from 'react';
import cytoscape from 'cytoscape';
import cola from 'cytoscape-cola';
import fcose from 'cytoscape-fcose';
import BlockActionStepper from './BlockActionStepper';
import useBlockAction from '../hooks/useBlockAction';

// Register layouts - fcose is industry standard for blockchain analytics (Chainalysis, TRM style)
cytoscape.use(cola);
cytoscape.use(fcose);

const CustomerClusterViewer = ({ contractAddress }) => {
    const [clusters, setClusters] = useState([]);
    const [methods, setMethods] = useState([]);
    const [selectedCluster, setSelectedCluster] = useState(null);
    const [clusterGraph, setClusterGraph] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingGraph, setLoadingGraph] = useState(false);
    const [error, setError] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);
    const [showMethods, setShowMethods] = useState(true);
    const [graphStats, setGraphStats] = useState(null); // Track full vs sampled stats
    const [clusterSearch, setClusterSearch] = useState(''); // Search filter for clusters
    const [totalClusters, setTotalClusters] = useState(0); // Total cluster count from API
    const [expandedMethod, setExpandedMethod] = useState(null); // Track which method is expanded
    const [methodInputs, setMethodInputs] = useState({}); // Cache method input data: method_sig -> data
    const [loadingMethodInputs, setLoadingMethodInputs] = useState(false); // Loading state for method inputs
    const [activeFilter, setActiveFilter] = useState(null); // Active filter: { methodSig, methodName, targetAddress, paramLabel }
    const [filteredClusters, setFilteredClusters] = useState(null); // Clusters matching the active filter
    const [loadingFilteredClusters, setLoadingFilteredClusters] = useState(false); // Loading state for filtered clusters

    // Block action hook
    const {
        isBlocking,
        actionStatus,
        blockCluster,
        blockWallet,
        isClusterBlocked,
        isWalletBlocked,
        resetAction,
        cleanup
    } = useBlockAction();

    const containerRef = useRef(null);
    const cyRef = useRef(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => cleanup();
    }, [cleanup]);

    // Graph API base URL
    const GRAPH_API_URL = process.env.REACT_APP_GRAPH_API_URL ||
        (window.location.hostname === 'localhost' && window.location.port === '5173' ? 'http://localhost:3001' : '/graph-api');

    const baseUrl = window.location.hostname === 'localhost'
        ? 'http://localhost:3000'
        : '/api';

    // Maximum nodes to render for performance
    // Higher limit allows seeing more of the cluster but may be slower on large graphs
    const MAX_DISPLAY_NODES = 5000;

    // Fetch clusters and methods related to this contract
    useEffect(() => {
        if (contractAddress) {
            fetchContractData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contractAddress]);

    const fetchContractData = async () => {
        setLoading(true);
        setError(null);

        try {
            const queryParams = '?limit=5000&offset=0'; // increase cluster page size so large clusters aren't trimmed
            // Fetch clusters and methods in parallel
            const [clustersRes, methodsRes] = await Promise.all([
                fetch(`${baseUrl}/v1/sybil/contract/${encodeURIComponent(contractAddress)}/clusters${queryParams}`, { credentials: 'include' })
                    .catch(() => fetch(`/api/v1/customer/sybil/clusters/${contractAddress}${queryParams}`, { credentials: 'include' })),
                fetch(`${baseUrl}/v1/sybil/contract/${encodeURIComponent(contractAddress)}/methods`, { credentials: 'include' })
            ]);

            // Process clusters
            if (clustersRes.ok) {
                const data = await clustersRes.json();
                setTotalClusters(data.total_clusters || data.clusters?.length || 0);
                if (data.clusters && data.clusters.length > 0) {
                    const clusterList = data.clusters.map(c => ({
                        id: c.cluster_id,
                        walletCount: c.wallet_count,
                        callCount: c.call_count || 0,
                        sybilCount: c.wallet_count,
                        typologies: c.typologies || (c.typology ? [c.typology] : []),
                        firstSeen: c.first_seen,
                        lastSeen: c.last_seen
                    }));

                    setClusters(clusterList);

                    if (clusterList.length > 0) {
                        setSelectedCluster(clusterList[0]);
                        fetchClusterGraph(clusterList[0]);
                    }
                } else {
                    setClusters([]);
                }
            } else {
                // Fallback to customer endpoint
                const fallbackRes = await fetch(`/api/v1/customer/sybil/clusters/${contractAddress}`, { credentials: 'include' });
                if (fallbackRes.ok) {
                    const data = await fallbackRes.json();
                    setTotalClusters(data.total_clusters || data.clusters?.length || 0);
                    if (data.clusters && data.clusters.length > 0) {
                        const clusterList = data.clusters.map(c => ({
                            id: c.cluster_id,
                            walletCount: c.wallet_count,
                            callCount: c.call_count || 0,
                            sybilCount: c.wallet_count,
                            typologies: c.typology ? [c.typology] : [],
                            firstSeen: c.first_seen,
                            lastSeen: c.last_seen
                        }));
                        setClusters(clusterList);

                        if (clusterList.length > 0) {
                            setSelectedCluster(clusterList[0]);
                            fetchClusterGraph(clusterList[0]);
                        }
                    }
                }
            }

            // Process methods
            if (methodsRes.ok) {
                const methodsData = await methodsRes.json();
                setMethods(methodsData.methods || []);
            }
        } catch (err) {
            console.error('Error fetching contract data:', err);
            setError('Failed to load cluster data');
        } finally {
            setLoading(false);
        }
    };

    const fetchClusterGraph = async (cluster) => {
        if (!cluster || !cluster.id) return;

        setLoadingGraph(true);
        setClusterGraph(null);
        setSelectedNode(null);
        setGraphStats(null);

        try {
            // Request server-side sampling for optimal performance on large clusters
            const response = await fetch(
                `${GRAPH_API_URL}/api/graph/full?community=${cluster.id}&chain=mainnet&max_nodes=${MAX_DISPLAY_NODES}`,
                { credentials: 'include' }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            if (data.success && data.graph) {
                const nodes = data.graph.nodes || [];
                const edges = data.graph.edges || [];

                // Use server-side sampling info if available
                const samplingInfo = data.sampling || {};
                const totalCommunityNodes = samplingInfo.total_nodes || data.stats?.full_community_size || nodes.length;

                setGraphStats({
                    displayedNodes: nodes.length,
                    displayedEdges: edges.length,
                    totalNodes: totalCommunityNodes,
                    sampled: samplingInfo.enabled || (totalCommunityNodes > nodes.length),
                    sybilInCluster: cluster.walletCount
                });

                setClusterGraph({ nodes, edges });
            }
        } catch (err) {
            console.error('Error fetching cluster graph:', err);
        } finally {
            setLoadingGraph(false);
        }
    };

    const handleClusterSelect = (cluster) => {
        setSelectedCluster(cluster);
        fetchClusterGraph(cluster);
    };

    // Fetch method input data (parameter breakdown) when expanding a method row
    const fetchMethodInputs = async (methodSig) => {
        // Check cache first
        if (methodInputs[methodSig]) {
            return methodInputs[methodSig];
        }

        setLoadingMethodInputs(true);
        try {
            const response = await fetch(
                `${baseUrl}/v1/sybil/contract/${encodeURIComponent(contractAddress)}/methods/${encodeURIComponent(methodSig)}/inputs`,
                { credentials: 'include' }
            );

            if (response.ok) {
                const data = await response.json();
                // Cache the result
                setMethodInputs(prev => ({
                    ...prev,
                    [methodSig]: data
                }));
                return data;
            }
        } catch (err) {
            console.error('Error fetching method inputs:', err);
        } finally {
            setLoadingMethodInputs(false);
        }
        return null;
    };

    // Handle method row click to expand/collapse
    const handleMethodClick = async (method) => {
        const methodSig = method.method_signature;

        if (expandedMethod === methodSig) {
            // Collapse if already expanded
            setExpandedMethod(null);
        } else {
            // Expand and fetch data
            setExpandedMethod(methodSig);
            await fetchMethodInputs(methodSig);
        }
    };

    // Format address for display
    const formatAddress = (addr) => {
        if (!addr || addr.length < 10) return addr;
        return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
    };

    // Fetch clusters filtered by method and target address
    const fetchFilteredClusters = async (methodSig, targetAddress, methodName, paramLabel) => {
        setLoadingFilteredClusters(true);
        try {
            const response = await fetch(
                `${baseUrl}/v1/sybil/contract/${encodeURIComponent(contractAddress)}/methods/${encodeURIComponent(methodSig)}/inputs/${encodeURIComponent(targetAddress)}/clusters`,
                { credentials: 'include' }
            );

            if (response.ok) {
                const data = await response.json();
                const clusterList = (data.clusters || []).map(c => ({
                    id: c.cluster_id,
                    walletCount: c.wallet_count,
                    matchingWallets: c.matching_wallets,
                    callCount: c.matching_wallets || 0,
                    sybilCount: c.wallet_count,
                    typologies: c.typologies || [],
                    firstSeen: c.first_seen,
                    lastSeen: c.last_seen
                }));
                setFilteredClusters(clusterList);

                // Set active filter info
                setActiveFilter({
                    methodSig,
                    methodName: methodName || methodSig,
                    targetAddress,
                    paramLabel: paramLabel || 'target'
                });

                // Select first filtered cluster if available
                if (clusterList.length > 0) {
                    setSelectedCluster(clusterList[0]);
                    fetchClusterGraph(clusterList[0]);
                }
            }
        } catch (err) {
            console.error('Error fetching filtered clusters:', err);
        } finally {
            setLoadingFilteredClusters(false);
        }
    };

    // Handle clicking on an input parameter row to filter clusters
    const handleInputClick = (e, input, method) => {
        e.stopPropagation(); // Prevent collapsing the method row
        fetchFilteredClusters(
            method.method_signature,
            input.address,
            method.method_name,
            input.param_label
        );
    };

    // Clear the active filter and show all clusters
    const clearFilter = () => {
        setActiveFilter(null);
        setFilteredClusters(null);
        // Re-select first cluster from full list
        if (clusters.length > 0) {
            setSelectedCluster(clusters[0]);
            fetchClusterGraph(clusters[0]);
        }
    };

    // Render cytoscape graph - PERFORMANCE OPTIMIZED
    useEffect(() => {
        if (!clusterGraph || !containerRef.current) return;

        // Clear existing graph
        if (cyRef.current) {
            cyRef.current.destroy();
            cyRef.current = null;
        }

        const elements = [];
        const nodeCount = clusterGraph.nodes?.length || 0;

        // Add nodes
        (clusterGraph.nodes || []).forEach(node => {
            elements.push({
                data: {
                    id: node.id,
                    label: node.id.substring(0, 8) + '...',
                    type: node.type || 'wallet',
                    fullAddress: node.id,
                    isSybil: node.is_sybil || (node.status && node.status.includes('Sybil')),
                    community: node.community,
                    status: node.status || '',
                    typology: node.typology || ''
                }
            });
        });

        // Add edges
        (clusterGraph.edges || []).forEach(edge => {
            elements.push({
                data: {
                    id: `${edge.source}-${edge.target}`,
                    source: edge.source,
                    target: edge.target
                }
            });
        });

        if (elements.length === 0) return;

        // Performance optimizations based on graph size
        const isLargeGraph = nodeCount > 200;
        const isVeryLargeGraph = nodeCount > 400;

        // Initialize Cytoscape with performance optimizations
        const cy = cytoscape({
            container: containerRef.current,
            elements,
            userZoomingEnabled: true,
            userPanningEnabled: true,
            minZoom: 0.1,
            maxZoom: 3,
            // Performance: disable expensive rendering for large graphs
            textureOnViewport: isLargeGraph,
            hideEdgesOnViewport: isVeryLargeGraph,
            hideLabelsOnViewport: isVeryLargeGraph,
            style: [
                // Default node style - clean, professional look
                {
                    selector: 'node',
                    style: {
                        'background-color': '#4a6fa5',
                        'background-opacity': 0.9,
                        'label': isLargeGraph ? '' : 'data(label)',
                        'font-size': 9,
                        'font-weight': 500,
                        'color': '#e8eef4',
                        'text-valign': 'bottom',
                        'text-margin-y': 6,
                        'text-outline-width': 1,
                        'text-outline-color': '#1a1a2e',
                        'width': isLargeGraph ? 18 : 32,
                        'height': isLargeGraph ? 18 : 32,
                        'border-width': isLargeGraph ? 1.5 : 2.5,
                        'border-color': '#3d5a80',
                        'border-opacity': 0.8,
                        // Subtle shadow effect
                        'overlay-opacity': 0
                    }
                },
                // Sybil nodes - red with emphasis
                {
                    selector: 'node[isSybil = true]',
                    style: {
                        'background-color': '#e63946',
                        'background-opacity': 0.95,
                        'border-color': '#d62839',
                        'border-width': isLargeGraph ? 2 : 3,
                        'width': isLargeGraph ? 22 : 36,
                        'height': isLargeGraph ? 22 : 36
                    }
                },
                // Edges - subtle, curved for organic look
                {
                    selector: 'edge',
                    style: {
                        'line-color': '#5c7cad',
                        'width': isLargeGraph ? 0.8 : 1.8,
                        'target-arrow-shape': isLargeGraph ? 'none' : 'triangle',
                        'target-arrow-color': '#5c7cad',
                        'curve-style': 'bezier', // Always bezier for organic flow
                        'arrow-scale': 0.7,
                        'opacity': isLargeGraph ? 0.35 : 0.65,
                        'line-cap': 'round'
                    }
                },
                // Selected node highlight
                {
                    selector: 'node:selected',
                    style: {
                        'border-width': 4,
                        'border-color': '#00d9ff',
                        'border-opacity': 1,
                        'label': 'data(label)',
                        'z-index': 999
                    }
                },
                // Hover state
                {
                    selector: 'node:active',
                    style: {
                        'overlay-color': '#00d9ff',
                        'overlay-opacity': 0.2
                    }
                }
            ],
            // FCOSE: Industry-standard layout for blockchain analytics (Chainalysis/TRM style)
            // Creates organic bubble/cluster shapes instead of cross patterns
            layout: {
                name: 'fcose',
                animate: false, // CRITICAL for performance
                fit: true,
                padding: 40,
                // Quality vs speed tradeoff
                quality: isLargeGraph ? 'default' : 'proof',
                // Randomize for variety - prevents identical layouts
                randomize: true,
                // Node separation - higher = more spread out bubbles
                nodeSeparation: isLargeGraph ? 100 : 150,
                // Ideal edge length - affects cluster tightness
                idealEdgeLength: isLargeGraph ? 80 : 120,
                // Edge elasticity - lower = tighter clusters
                edgeElasticity: 0.45,
                // Nesting factor for compound nodes
                nestingFactor: 0.1,
                // Gravity - lower = more spread out, organic shapes
                gravity: 0.25,
                // Gravity range - affects how far gravity reaches
                gravityRange: 3.8,
                // Repulsion - higher = nodes push apart more (prevents overlap)
                nodeRepulsion: isLargeGraph ? 6500 : 8500,
                // Number of iterations - more = better quality but slower
                numIter: isLargeGraph ? 2500 : 5000,
                // Tile disconnected components
                tile: true,
                // Packing - 0 = rectangular, 1 = circular (more organic)
                packComponents: true,
                // Step size for layout algorithm
                step: "all"
            }
        });

        // Click handler
        cy.on('tap', 'node', function (evt) {
            const node = evt.target;
            const data = node.data();
            setSelectedNode({
                address: data.fullAddress,
                isSybil: data.isSybil,
                status: data.status,
                typology: data.typology,
                connections: node.degree()
            });
        });

        cy.on('tap', function (evt) {
            if (evt.target === cy) {
                setSelectedNode(null);
            }
        });

        // Show label on hover for large graphs
        if (isLargeGraph) {
            cy.on('mouseover', 'node', function (evt) {
                evt.target.style('label', evt.target.data('label'));
            });
            cy.on('mouseout', 'node', function (evt) {
                if (!evt.target.selected()) {
                    evt.target.style('label', '');
                }
            });
        }

        cy.ready(() => {
            cy.fit(undefined, 30);
        });

        cyRef.current = cy;

        return () => {
            if (cyRef.current) {
                cyRef.current.destroy();
                cyRef.current = null;
            }
        };
    }, [clusterGraph]);

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-secondary">Loading cluster data for your contract...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
            </div>
        );
    }

    if (clusters.length === 0) {
        return (
            <div className="text-center py-5">
                <i className="bi bi-shield-check text-success" style={{ fontSize: '4rem' }}></i>
                <h4 className="text-white mt-3">No Sybil Clusters Detected</h4>
                <p className="text-secondary">
                    No Sybil clusters have been detected interacting with your contract.
                </p>
            </div>
        );
    }

    const formatNumber = (num) => {
        return (num || 0).toLocaleString();
    };

    return (
        <div>
            {/* Methods Called by Sybils */}
            {methods.length > 0 && (
                <div className="bg-dark border border-secondary rounded p-3 mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="text-white mb-0">
                            <i className="bi bi-code-slash text-info me-2"></i>
                            Methods Called by Sybils ({methods.length})
                        </h6>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setShowMethods(!showMethods)}
                        >
                            <i className={`bi bi-chevron-${showMethods ? 'up' : 'down'}`}></i>
                        </button>
                    </div>
                    {showMethods && (
                        <div className="table-responsive">
                            <table className="table table-dark table-hover table-sm small">
                                <thead>
                                    <tr>
                                        <th style={{ width: '24px' }}></th>
                                        <th>Method Signature</th>
                                        <th>Method Name</th>
                                        <th className="text-end">Calls</th>
                                        <th className="text-end">Wallets</th>
                                        <th className="text-end">Clusters</th>
                                        <th>Activity Range</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {methods.map((method, idx) => (
                                        <React.Fragment key={idx}>
                                            <tr
                                                onClick={() => handleMethodClick(method)}
                                                style={{ cursor: 'pointer' }}
                                                className={expandedMethod === method.method_signature ? 'table-active' : ''}
                                            >
                                                <td>
                                                    <i className={`bi bi-chevron-${expandedMethod === method.method_signature ? 'down' : 'right'} text-secondary`}></i>
                                                </td>
                                                <td><code className="text-warning">{method.method_signature}</code></td>
                                                <td>{method.method_name || '-'}</td>
                                                <td className="text-end">
                                                    <span className="badge bg-primary">{formatNumber(method.call_count)}</span>
                                                </td>
                                                <td className="text-end text-secondary">{formatNumber(method.unique_wallets)}</td>
                                                <td className="text-end text-secondary">{formatNumber(method.unique_clusters)}</td>
                                                <td className="text-secondary">
                                                    {method.first_seen ? new Date(method.first_seen).toLocaleDateString() : '-'}
                                                    {' → '}
                                                    {method.last_seen ? new Date(method.last_seen).toLocaleDateString() : '-'}
                                                </td>
                                            </tr>
                                            {/* Expanded row showing input data breakdown */}
                                            {expandedMethod === method.method_signature && (
                                                <tr>
                                                    <td colSpan="7" className="p-0">
                                                        <div className="bg-secondary-subtle p-3 border-top border-bottom border-dark">
                                                            {loadingMethodInputs ? (
                                                                <div className="text-center py-3">
                                                                    <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                                                                    <span className="text-secondary">Loading input data...</span>
                                                                </div>
                                                            ) : methodInputs[method.method_signature] ? (
                                                                <div>
                                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                                        <h6 className="text-info mb-0 small">
                                                                            {methodInputs[method.method_signature].is_uniswap_swap ? (
                                                                                <>
                                                                                    <i className="bi bi-coin me-2"></i>
                                                                                    Target Tokens {methodInputs[method.method_signature].swap_direction === 'buy' ? 'Bought' : 'Sold'}
                                                                                    {methodInputs[method.method_signature].swap_direction && (
                                                                                        <span className={`badge ms-2 ${methodInputs[method.method_signature].swap_direction === 'buy' ? 'bg-success' : 'bg-warning text-dark'}`} style={{ fontSize: '0.65rem' }}>
                                                                                            {methodInputs[method.method_signature].swap_direction === 'buy' ? '↓ BUY' : '↑ SELL'}
                                                                                        </span>
                                                                                    )}
                                                                                </>
                                                                            ) : methodInputs[method.method_signature].is_liquidity_method ? (
                                                                                <>
                                                                                    <i className="bi bi-droplet me-2"></i>
                                                                                    Liquidity Pool Tokens
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <i className="bi bi-diagram-2 me-2"></i>
                                                                                    Input Parameters for {methodInputs[method.method_signature].method_name}
                                                                                </>
                                                                            )}
                                                                        </h6>
                                                                        <span className="badge bg-secondary">
                                                                            {methodInputs[method.method_signature].input_count} unique {methodInputs[method.method_signature].is_uniswap_swap ? 'tokens' : 'targets'}
                                                                        </span>
                                                                    </div>
                                                                    {methodInputs[method.method_signature].is_uniswap_swap && (
                                                                        <div className="alert alert-info py-1 px-2 mb-2" style={{ fontSize: '0.7rem' }}>
                                                                            <i className="bi bi-info-circle me-1"></i>
                                                                            {methodInputs[method.method_signature].swap_direction === 'buy' 
                                                                                ? 'Tokens purchased by sybil wallets. High activity may indicate wash trading.'
                                                                                : 'Tokens sold by sybil wallets. High sell activity may indicate pump-and-dump.'}
                                                                        </div>
                                                                    )}
                                                                    {methodInputs[method.method_signature].inputs?.length > 0 ? (
                                                                        <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                                                            <table className="table table-dark table-sm mb-0" style={{ fontSize: '0.75rem' }}>
                                                                                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#212529', zIndex: 1 }}>
                                                                                    <tr>
                                                                                        <th>
                                                                                            {methodInputs[method.method_signature].is_uniswap_swap ? (
                                                                                                methodInputs[method.method_signature].swap_direction === 'buy' ? 'Token Bought' : 'Token Sold'
                                                                                            ) : methodInputs[method.method_signature].is_liquidity_method ? (
                                                                                                'Pool Token'
                                                                                            ) : methodInputs[method.method_signature].inputs[0]?.param_label === 'recipient' ? (
                                                                                                'Recipient Address'
                                                                                            ) : methodInputs[method.method_signature].inputs[0]?.param_label === 'spender' ? (
                                                                                                'Approved Spender'
                                                                                            ) : (
                                                                                                'Target Address'
                                                                                            )}
                                                                                        </th>
                                                                                        <th className="text-end">{methodInputs[method.method_signature].is_uniswap_swap ? 'Swaps' : 'Calls'}</th>
                                                                                        <th className="text-end">Wallets</th>
                                                                                        <th className="text-end">Clusters</th>
                                                                                        <th>Activity</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {methodInputs[method.method_signature].inputs.map((input, iIdx) => (
                                                                                        <tr
                                                                                            key={iIdx}
                                                                                            onClick={(e) => handleInputClick(e, input, method)}
                                                                                            style={{ cursor: 'pointer' }}
                                                                                            className={activeFilter?.targetAddress === input.address && activeFilter?.methodSig === method.method_signature ? 'table-active' : ''}
                                                                                            title="Click to filter clusters by this target"
                                                                                        >
                                                                                            <td>
                                                                                                {methodInputs[method.method_signature].is_uniswap_swap && (
                                                                                                    <span className={`badge me-1 ${input.swap_direction === 'buy' ? 'bg-success' : 'bg-warning text-dark'}`} style={{ fontSize: '0.55rem' }}>
                                                                                                        {input.swap_direction === 'buy' ? 'BUY' : 'SELL'}
                                                                                                    </span>
                                                                                                )}
                                                                                                <code className="text-info" style={{ fontSize: '0.7rem', wordBreak: 'break-all' }}>{input.address}</code>
                                                                                                <button
                                                                                                    className="btn btn-link btn-sm p-0 ms-1"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        navigator.clipboard.writeText(input.address);
                                                                                                    }}
                                                                                                    title="Copy address"
                                                                                                >
                                                                                                    <i className="bi bi-clipboard text-secondary" style={{ fontSize: '0.65rem' }}></i>
                                                                                                </button>
                                                                                                <a 
                                                                                                    href={`https://etherscan.io/token/${input.address}`}
                                                                                                    target="_blank"
                                                                                                    rel="noopener noreferrer"
                                                                                                    className="btn btn-link btn-sm p-0 ms-1"
                                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                                    title="View on Etherscan"
                                                                                                >
                                                                                                    <i className="bi bi-box-arrow-up-right text-secondary" style={{ fontSize: '0.6rem' }}></i>
                                                                                                </a>
                                                                                                <i className="bi bi-funnel text-warning ms-1" style={{ fontSize: '0.6rem' }} title="Click to filter clusters"></i>
                                                                                            </td>
                                                                                            <td className="text-end">
                                                                                                <span className="badge bg-warning text-dark">{formatNumber(input.call_count)}</span>
                                                                                            </td>
                                                                                            <td className="text-end text-secondary">{formatNumber(input.unique_senders)}</td>
                                                                                            <td className="text-end">
                                                                                                <span className="badge bg-danger">{formatNumber(input.unique_clusters)}</span>
                                                                                            </td>
                                                                                            <td className="text-secondary">
                                                                                                {input.first_call ? new Date(input.first_call * 1000).toLocaleDateString() : '-'}
                                                                                                {' → '}
                                                                                                {input.last_call ? new Date(input.last_call * 1000).toLocaleDateString() : '-'}
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-center text-secondary py-2">
                                                                            <i className="bi bi-info-circle me-2"></i>
                                                                            No input data available for this method
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div className="text-center text-secondary py-2">
                                                                    <i className="bi bi-info-circle me-2"></i>
                                                                    No input data available
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <div className="row g-4">
                {/* Cluster List - Left Side */}
                <div className="col-lg-4">
                    <div className="bg-dark border border-secondary rounded p-3">
                        {/* Active Filter Indicator */}
                        {activeFilter && (
                            <div className="alert alert-warning py-2 px-3 mb-3 d-flex justify-content-between align-items-center" style={{ fontSize: '0.8rem' }}>
                                <div>
                                    <i className="bi bi-funnel-fill me-2"></i>
                                    <strong>Filtered by:</strong>{' '}
                                    <span className="text-dark">
                                        {activeFilter.methodName} → {activeFilter.paramLabel}: {formatAddress(activeFilter.targetAddress)}
                                    </span>
                                </div>
                                <button
                                    className="btn btn-sm btn-outline-dark py-0 px-2"
                                    onClick={clearFilter}
                                    title="Clear filter"
                                >
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>
                        )}

                        {loadingFilteredClusters && (
                            <div className="text-center py-3">
                                <div className="spinner-border spinner-border-sm text-warning me-2"></div>
                                <span className="text-secondary">Filtering clusters...</span>
                            </div>
                        )}

                        <h6 className="text-white mb-2">
                            <i className="bi bi-diagram-3 text-danger me-2"></i>
                            {activeFilter ? (
                                <>Matching Clusters ({filteredClusters?.length || 0})</>
                            ) : (
                                <>Sybil Clusters ({clusters.length}{totalClusters > clusters.length ? ` of ${totalClusters}` : ''})</>
                            )}
                        </h6>
                        <p className="text-secondary small mb-2">
                            {activeFilter
                                ? 'Clusters where wallets made the selected call'
                                : 'Select a cluster to view its network graph'}
                        </p>

                        {/* Cluster search filter */}
                        <div className="mb-3">
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-secondary border-secondary">
                                    <i className="bi bi-search text-white"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control bg-dark text-white border-secondary"
                                    placeholder="Search by cluster ID..."
                                    value={clusterSearch}
                                    onChange={(e) => setClusterSearch(e.target.value)}
                                />
                                {clusterSearch && (
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => setClusterSearch('')}
                                    >
                                        <i className="bi bi-x"></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
                            {(activeFilter ? filteredClusters : clusters)
                                ?.filter(c => !clusterSearch || c.id.toString().includes(clusterSearch))
                                .map((cluster, idx) => (
                                    <div
                                        key={cluster.id}
                                        className={`p-3 mb-2 rounded cursor-pointer ${selectedCluster?.id === cluster.id
                                            ? 'bg-secondary-subtle border border-primary border-2 shadow-sm'
                                            : 'bg-secondary-subtle border border-secondary'
                                            }`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleClusterSelect(cluster)}
                                    >
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div>
                                                <span className="text-white fw-bold">Cluster #{cluster.id}</span>
                                            </div>
                                            <span className="badge bg-danger">
                                                {cluster.walletCount} sybils
                                            </span>
                                        </div>
                                        <div className="small text-secondary">
                                            {activeFilter && cluster.matchingWallets && (
                                                <div className="mb-1 text-warning">
                                                    <i className="bi bi-funnel me-1"></i>
                                                    {cluster.matchingWallets} wallets made this call
                                                </div>
                                            )}
                                            <div className="mb-1">
                                                <i className="bi bi-people me-1"></i>
                                                {cluster.walletCount} wallets in cluster
                                            </div>
                                            {cluster.callCount > 0 && !activeFilter && (
                                                <div className="mb-1">
                                                    <i className="bi bi-arrow-repeat me-1"></i>
                                                    {formatNumber(cluster.callCount)} total calls
                                                </div>
                                            )}
                                            <div className="mb-1">
                                                <i className="bi bi-calendar me-1"></i>
                                                {cluster.firstSeen ? new Date(cluster.firstSeen).toLocaleDateString() : '?'}
                                                {' - '}
                                                {cluster.lastSeen ? new Date(cluster.lastSeen).toLocaleDateString() : '?'}
                                            </div>
                                            {cluster.typologies && cluster.typologies.length > 0 && (
                                                <div className="mt-1">
                                                    {cluster.typologies.filter(t => t).slice(0, 2).map((t, i) => (
                                                        <span key={i} className="badge bg-warning text-dark me-1" style={{ fontSize: '0.65rem' }}>
                                                            {t}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>

                    {/* Legend */}
                    <div className="bg-dark border border-secondary rounded p-3 mt-3">
                        <h6 className="text-secondary mb-3">Legend</h6>
                        <div className="small">
                            <div className="d-flex align-items-center mb-2">
                                <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#dc3545', marginRight: 10 }}></div>
                                <span className="text-white">Sybil Wallet</span>
                            </div>
                            <div className="d-flex align-items-center mb-2">
                                <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#6c757d', marginRight: 10 }}></div>
                                <span className="text-white">Normal Wallet</span>
                            </div>
                            <div className="d-flex align-items-center">
                                <div style={{ width: 16, height: 2, backgroundColor: '#0d6efd', marginRight: 10 }}></div>
                                <span className="text-white">Transfer</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Graph View - Right Side */}
                <div className="col-lg-8">
                    {/* Cluster Overview - shown when a cluster is selected */}
                    {selectedCluster && (
                        <div className="bg-dark border border-warning rounded p-3 mb-3">
                            <h6 className="text-warning mb-3">
                                <i className="bi bi-diagram-3 me-2"></i>
                                Cluster Overview — #{selectedCluster.id}
                            </h6>
                            <div className="row g-3">
                                {/* Typology */}
                                <div className="col-md-6">
                                    <div className="small">
                                        <span className="text-secondary d-block mb-1">Typology:</span>
                                        {selectedCluster.typologies && selectedCluster.typologies.filter(t => t).length > 0 ? (
                                            <div className="d-flex flex-wrap gap-1">
                                                {selectedCluster.typologies.filter(t => t).map((t, i) => (
                                                    <span key={i} className="badge bg-warning text-dark">
                                                        {t}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-secondary">—</span>
                                        )}
                                    </div>
                                </div>
                                {/* Activity Period */}
                                <div className="col-md-6">
                                    <div className="small">
                                        <span className="text-secondary d-block mb-1">Activity Period:</span>
                                        <span className="text-white">
                                            <i className="bi bi-calendar-range me-1 text-secondary"></i>
                                            {selectedCluster.firstSeen ? new Date(selectedCluster.firstSeen).toLocaleDateString() : '?'}
                                            {' — '}
                                            {selectedCluster.lastSeen ? new Date(selectedCluster.lastSeen).toLocaleDateString() : '?'}
                                        </span>
                                    </div>
                                </div>
                                {/* Total Wallets in Cluster */}
                                <div className="col-md-4">
                                    <div className="bg-secondary-subtle rounded p-2 text-center">
                                        <div className="text-secondary small mb-1">Total Wallets in Cluster</div>
                                        <div className="text-white fw-bold fs-5">
                                            {/* Use graph stats if available and has nodes, otherwise fall back to cluster wallet count */}
                                            {graphStats && graphStats.totalNodes > 0
                                                ? formatNumber(graphStats.totalNodes)
                                                : formatNumber(selectedCluster.walletCount)}
                                        </div>
                                    </div>
                                </div>
                                {/* Wallets Interacted with Contract */}
                                <div className="col-md-4">
                                    <div className="bg-secondary-subtle rounded p-2 text-center">
                                        <div className="text-secondary small mb-1">Interacted w/ Contract</div>
                                        <div className="text-danger fw-bold fs-5">
                                            {formatNumber(selectedCluster.walletCount)}
                                        </div>
                                    </div>
                                </div>
                                {/* Total Calls */}
                                <div className="col-md-4">
                                    <div className="bg-secondary-subtle rounded p-2 text-center">
                                        <div className="text-secondary small mb-1">Total Calls</div>
                                        <div className="text-info fw-bold fs-5">
                                            {formatNumber(selectedCluster.callCount)}
                                        </div>
                                    </div>
                                </div>

                                {/* Block Cluster Button */}
                                <div className="col-12 mt-2">
                                    {isClusterBlocked(selectedCluster.id) ? (
                                        <div className="d-flex align-items-center justify-content-center text-success py-2">
                                            <i className="bi bi-shield-fill-check me-2 fs-5"></i>
                                            <span className="fw-bold">Blocked ✅</span>
                                        </div>
                                    ) : (
                                        <button
                                            className="btn btn-danger w-100"
                                            onClick={() => blockCluster(selectedCluster.id)}
                                            disabled={isBlocking}
                                        >
                                            {isBlocking && actionStatus?.clusterId === selectedCluster.id ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Blocking...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-shield-fill-x me-2"></i>
                                                    Block Cluster
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Block Action Stepper (shown during/after block operation) */}
                            {actionStatus && actionStatus.kind === 'cluster' && actionStatus.clusterId === selectedCluster.id && (
                                <div className="mt-3">
                                    <BlockActionStepper
                                        status={actionStatus.status}
                                        txHash={actionStatus.txHash}
                                        error={actionStatus.error}
                                        addressCount={actionStatus.addressCount}
                                        explorerUrl={actionStatus.explorerUrl}
                                        onClose={actionStatus.status === 'CONFIRMED_ONCHAIN' || actionStatus.status === 'FAILED' ? resetAction : null}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Selected Node Info */}
                    {selectedNode && (
                        <div className="bg-dark border border-info rounded p-3 mb-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="text-info mb-0">
                                    <i className="bi bi-circle-fill me-2" style={{ color: selectedNode.isSybil ? '#dc3545' : '#6c757d' }}></i>
                                    Wallet Details
                                </h6>
                                {/* Block Wallet Button */}
                                {selectedNode.isSybil && (
                                    isWalletBlocked(selectedNode.address) ? (
                                        <span className="badge bg-success">
                                            <i className="bi bi-shield-fill-check me-1"></i>
                                            Blocked
                                        </span>
                                    ) : (
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => blockWallet(selectedNode.address, selectedCluster?.id)}
                                            disabled={isBlocking}
                                            title="Block this wallet on-chain"
                                        >
                                            {isBlocking && actionStatus?.address === selectedNode.address ? (
                                                <span className="spinner-border spinner-border-sm"></span>
                                            ) : (
                                                <>
                                                    <i className="bi bi-shield-fill-x me-1"></i>
                                                    Block Wallet
                                                </>
                                            )}
                                        </button>
                                    )
                                )}
                            </div>
                            <div className="small">
                                <div className="mb-1">
                                    <span className="text-secondary">Address:</span>
                                    <code className="text-info ms-2" style={{ fontSize: '0.75rem' }}>{selectedNode.address}</code>
                                    <button
                                        className="btn btn-link btn-sm p-0 ms-2"
                                        onClick={() => navigator.clipboard.writeText(selectedNode.address)}
                                    >
                                        <i className="bi bi-clipboard text-secondary"></i>
                                    </button>
                                </div>
                                {selectedNode.isSybil && (
                                    <div className="text-danger mb-1">
                                        <i className="bi bi-exclamation-triangle me-1"></i>
                                        Sybil Detected
                                    </div>
                                )}
                                {selectedNode.typology && (
                                    <div className="mb-1">
                                        <span className="text-secondary">Typology:</span>
                                        <span className="text-warning ms-2">{selectedNode.typology}</span>
                                    </div>
                                )}
                                <div>
                                    <span className="text-secondary">Connections:</span>
                                    <span className="text-white ms-2">{selectedNode.connections}</span>
                                </div>
                            </div>

                            {/* Block Action Stepper for Wallet (shown during/after block operation) */}
                            {actionStatus && actionStatus.kind === 'wallet' && actionStatus.address === selectedNode.address && (
                                <div className="mt-3">
                                    <BlockActionStepper
                                        status={actionStatus.status}
                                        txHash={actionStatus.txHash}
                                        error={actionStatus.error}
                                        addressCount={actionStatus.addressCount}
                                        explorerUrl={actionStatus.explorerUrl}
                                        onClose={actionStatus.status === 'CONFIRMED_ONCHAIN' || actionStatus.status === 'FAILED' ? resetAction : null}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Graph Container */}
                    <div className="bg-dark border border-secondary rounded" style={{ position: 'relative' }}>
                        {!selectedCluster && (
                            <div className="d-flex align-items-center justify-content-center" style={{ height: '500px' }}>
                                <div className="text-center">
                                    <i className="bi bi-arrow-left text-secondary" style={{ fontSize: '3rem' }}></i>
                                    <h5 className="text-secondary mt-3">Select a Cluster</h5>
                                    <p className="text-secondary">Choose a cluster from the list to view its graph</p>
                                </div>
                            </div>
                        )}

                        {loadingGraph && (
                            <div className="d-flex align-items-center justify-content-center" style={{ height: '500px' }}>
                                <div className="text-center">
                                    <div className="spinner-border text-primary"></div>
                                    <p className="text-white mt-3">Loading cluster graph...</p>
                                </div>
                            </div>
                        )}

                        {/* Show message when graph data is not yet synced to Neo4j */}
                        {selectedCluster && !loadingGraph && (!clusterGraph || clusterGraph.nodes?.length === 0) && (
                            <div className="d-flex align-items-center justify-content-center" style={{ height: '500px' }}>
                                <div className="text-center px-4">
                                    <i className="bi bi-diagram-3 text-warning" style={{ fontSize: '3rem' }}></i>
                                    <h5 className="text-white mt-3">Graph Visualization Pending</h5>
                                    <p className="text-secondary">
                                        This cluster has <strong className="text-danger">{selectedCluster.walletCount} Sybil wallets</strong> detected,
                                        but graph visualization data is still being processed.
                                    </p>
                                    <p className="text-secondary small">
                                        <i className="bi bi-info-circle me-1"></i>
                                        The cluster data is available in the API. Graph rendering will be available once the visualization sync completes.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div
                            ref={containerRef}
                            style={{
                                height: '500px',
                                width: '100%',
                                display: clusterGraph && !loadingGraph ? 'block' : 'none'
                            }}
                        />

                        {selectedCluster && clusterGraph && !loadingGraph && graphStats && (
                            <div className="position-absolute bottom-0 start-0 end-0 m-2 p-2 bg-dark bg-opacity-75 rounded d-flex justify-content-between align-items-center">
                                <div className="small">
                                    <span className="text-white fw-bold">Cluster #{selectedCluster.id}</span>
                                    {graphStats.sampled ? (
                                        <span className="text-warning ms-2">
                                            <i className="bi bi-lightning-charge me-1"></i>
                                            Showing {graphStats.displayedNodes} of {graphStats.totalNodes.toLocaleString()} wallets in community
                                        </span>
                                    ) : (
                                        <span className="text-secondary ms-2">
                                            {graphStats.displayedNodes} wallets • {graphStats.displayedEdges} connections
                                        </span>
                                    )}
                                </div>
                                <div className="small">
                                    <span className="badge bg-danger">
                                        <i className="bi bi-exclamation-triangle me-1"></i>
                                        {selectedCluster.walletCount} interacted with your contract
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mt-2 small text-secondary">
                        <i className="bi bi-info-circle me-2"></i>
                        Click on nodes to see details • Drag to pan • Scroll to zoom
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerClusterViewer;

